import { useParams, Navigate, Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CallbackForm } from '../components/CallbackForm';
import { SERVICES_DATA } from '../data/servicesData';
import { translations } from '../translations';

const RELATED_AR = { bosphorus: 'البوسفور', beyoglu: 'بيوغلو', kadikoy: 'قاضي كوي', sultanahmet: 'السلطان أحمد', karakoy: 'كاراكوي' };

export default function ServicePage({ language, setLanguage }) {
  const { slug } = useParams();
  const t = translations[language];
  const service = SERVICES_DATA.find(s => s.slug === slug);
  const isAr = language === 'ar';

  if (!service) return <Navigate to="/tours" replace />;

  const title = isAr ? (service.titleAr || service.title) : service.title;
  const desc = isAr ? (service.descriptionAr || service.description) : service.description;
  const included = isAr ? (service.includedAr || service.included) : service.included;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "provider": { "@type": "Organization", "name": "Viaturkey", "url": "https://viaturkey.net" },
    "areaServed": { "@type": "City", "name": "Istanbul" },
    "description": service.metaDescription,
    "url": `https://viaturkey.net/tours/${service.slug}`
  };

  return (
    <div className="service-page" dir={isAr ? 'rtl' : 'ltr'} data-testid="service-page">
      <SEOHead
        title={isAr ? (service.metaTitleAr || service.metaTitle) : service.metaTitle}
        description={isAr ? (service.metaDescriptionAr || service.metaDescription) : service.metaDescription}
        canonical={`https://viaturkey.net/tours/${service.slug}`}
        schema={schema}
      />
      <Header language={language} t={t} />

      <section className="sp-hero" data-testid="sp-hero" style={{backgroundImage: `url(${service.heroImage})`}}>
        <div className="sp-hero-overlay"></div>
        <div className="sp-hero-content">
          <h1>{title}</h1>
        </div>
      </section>

      <div className="sp-container">
        <div className="sp-body">
          <p className="sp-desc">{desc}</p>

          <h2>{isAr ? 'ما يشمله' : "What's Included"}</h2>
          <ul className="sp-included" data-testid="sp-included">
            {included.map((item, i) => (
              <li key={i}><i className="fas fa-check-circle"></i> {item}</li>
            ))}
          </ul>

          {service.relatedDestinations.length > 0 && (
            <div className="sp-related">
              <h2>{isAr ? 'استكشف المناطق ذات الصلة' : 'Explore Related Areas'}</h2>
              <div className="sp-related-links">
                {service.relatedDestinations.map(d => (
                  <Link key={d} to={`/istanbul/${d}`} className="sp-related-link" data-testid={`related-${d}`}>
                    <i className="fas fa-map-marker-alt"></i> {isAr ? (RELATED_AR[d] || d) : d.charAt(0).toUpperCase() + d.slice(1)}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sp-cta" data-testid="sp-cta">
          <h2>{isAr ? 'هل أنت مستعد للحجز؟' : 'Ready to Book?'}</h2>
          <p>{isAr ? 'تواصل معنا مباشرة على واتساب للحصول على عرض سعر مخصص. خدمة مباشرة بدون وسطاء وبدون رسوم مخفية.' : 'Contact us directly on WhatsApp for a personalized quote. No middlemen, no hidden fees — just warm, professional service.'}</p>
          <a href={`https://wa.me/905075337545?text=${encodeURIComponent(`Hi Viaturkey! I'm interested in ${service.title}.`)}`} target="_blank" rel="noopener noreferrer" className="sp-cta-btn" data-testid="sp-whatsapp-btn">
            <i className="fab fa-whatsapp"></i> {isAr ? 'راسلنا على واتساب' : 'Message Us on WhatsApp'}
          </a>
        </div>

        <CallbackForm language={language} />
      </div>

      <Footer t={t} />
    </div>
  );
}
